/**
 * QueryApi — methods that READ data without mutating it.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │  PUT IT HERE if the request does NOT change data on the server.    │
 * │                                                                    │
 * │  This is about DATA MUTATION, not HTTP method:                     │
 * │    GET  /block/list          → here (reads a list)               │
 * │    POST /block/search        → here (reads via search, no mutation)│
 * │    POST /ingest               → command-api (creates data)        │
 * │    POST /assistant            → command-api (creates a session)   │
 * │                                                                    │
 * │  For anything that creates, updates, or deletes, use command-api. │
 * └─────────────────────────────────────────────────────────────────────┘
 */
import { apiGet, apiPost } from "./base-query-api";
export async function listBlocks(options) {
    const params = {};
    if (options?.limit !== undefined)
        params.limit = options.limit;
    if (options?.offset !== undefined)
        params.offset = options.offset;
    if (options?.type)
        params.type = options.type;
    if (options?.capturedAfter)
        params.capturedAfter = options.capturedAfter;
    if (options?.capturedBefore)
        params.capturedBefore = options.capturedBefore;
    if (options?.surfaceAfter)
        params.surfaceAfter = options.surfaceAfter;
    if (options?.surfaceBefore)
        params.surfaceBefore = options.surfaceBefore;
    if (options?.orderBy)
        params.orderBy = options.orderBy;
    if (options?.order)
        params.order = options.order;
    const res = await apiGet("/block/list", params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list blocks");
    }
    return res.data;
}
export async function getBlockTimeline() {
    const res = await apiGet("/block/timeline");
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to fetch block timeline");
    }
    return res.data;
}
export async function getRandomBlocks(options) {
    const params = {};
    if (options?.limit !== undefined)
        params.limit = options.limit;
    if (options?.type)
        params.type = options.type;
    const res = await apiGet("/block/random", params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to fetch random blocks");
    }
    return res.data;
}
export async function getBlockDetails(blockId) {
    const res = await apiGet(`/block/${blockId}/details`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to get block details");
    }
    return res.data;
}
export async function fetchBlockEditMeta(blockId) {
    const res = await apiGet(`/block/${blockId}/details`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to fetch block edit meta");
    }
    return { editLock: res.data.editLock, lastEdit: res.data.lastEdit };
}
/**
 * Fetch a single block as a BlockEnvelope (uses the details endpoint,
 * discards tags/taxonomies). Returns undefined on any failure.
 */
export async function fetchBlockEnvelope(blockId) {
    try {
        const res = await apiGet(`/block/${blockId}/details`);
        if (!res.success || !res.data?.block)
            return undefined;
        return { block: res.data.block };
    }
    catch {
        return undefined;
    }
}
export async function searchBlocks(options) {
    const res = await apiPost("/block/search", options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to search blocks");
    }
    return res.data;
}
/**
 * Index-only block search: full-text over block title/summary + artifact
 * content (both GIN-indexed tsvector), fused with RRF — no query embedding and
 * no LLM re-rank. Built for type-ahead UIs; far faster than `searchBlocks`.
 */
export async function searchBlocksInstant(options) {
    const res = await apiPost("/block/search/instant", options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to search blocks");
    }
    return res.data;
}
export async function listMentions(filter = "all", limit = 30) {
    const res = await apiGet("/mention/list", { filter, limit });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list mentions");
    }
    return res.data.mentions;
}
export async function markMentionRead(mentionId) {
    await apiPost(`/mention/${mentionId}/read`, {});
}
/**
 * Send a query to the conversational assistant. Always returns a smart-collection
 * shape; the client opens it as a transient tab and lets the user save it.
 */
export async function sendAssistantQuery(message) {
    const res = await apiPost("/assistant/query", { message });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to send assistant query");
    }
    return res.data;
}
/**
 * Ask the assistant a question scoped to one Collection — or to an ad-hoc
 * set of blocks (smart-collection tabs, future "ask about this selection"
 * surfaces). The server fetches those blocks (and artifact text for the
 * most recent few) and answers grounded in them. Read-shaped: no
 * server-side state is mutated; the caller owns the transcript and
 * replays history on each turn.
 */
export async function chatWithCollection(options) {
    // Build the wire body by mode. The server's Zod schema is a union
    // (either `{ collectionId, ... }` or `{ blockIds, title, ... }`) and
    // rejects requests that mix the two; we mirror that strictness here
    // so a caller passing both fields fails fast in the type system.
    const body = { message: options.message };
    if (options.history && options.history.length > 0) {
        body.history = options.history;
    }
    if (options.collectionId !== undefined) {
        body.collectionId = options.collectionId;
    }
    else {
        body.blockIds = options.blockIds;
        body.title = options.title;
    }
    const res = await apiPost("/assistant/collection-chat", body);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to send collection chat message");
    }
    return res.data;
}
/**
 * Ask a question over an ad-hoc block set and get a structured board back: a
 * prose answer, the relevant blocks each with a one-line caption (the reason
 * it's relevant), and a few thematic groups. Read-shaped — nothing is persisted
 * server-side. Powers the viewer's temporary query canvas.
 */
export async function buildCollectionCanvas(options) {
    const res = await apiPost("/assistant/collection-canvas", options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to build canvas");
    }
    return res.data;
}
export async function listTaxonomies(options) {
    const params = {};
    if (options?.limit !== undefined)
        params.limit = options.limit;
    if (options?.offset !== undefined)
        params.offset = options.offset;
    if (options?.type)
        params.type = options.type;
    if (options?.sortBy)
        params.sortBy = options.sortBy;
    const res = await apiGet("/taxonomy/list", params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list taxonomies");
    }
    return res.data;
}
export async function getTaxonomyDetails(taxonomyId) {
    const res = await apiGet(`/taxonomy/${taxonomyId}/details`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to get taxonomy details");
    }
    return res.data;
}
export async function searchTaxonomies(options) {
    const res = await apiPost("/taxonomy/search", options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to search taxonomies");
    }
    return res.data;
}
/**
 * @deprecated Use `fetchPins({type: "account"}, "feed")` instead. The
 * `/v1/account/preferences` endpoint is kept for one release as a
 * back-compat proxy onto the new Pin model; new code should hit the
 * `/v1/pin` endpoint directly.
 */
export async function fetchAccountHomepagePins() {
    const res = await apiGet("/account/preferences");
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to load homepage pins");
    }
    return res.data.pins;
}
/**
 * List pins for a (scope, surface) slot, in render order.
 *
 * - account scope: surface is "feed" or "tab"
 * - collection scope: surface is "default" (the only one today)
 */
export async function fetchPins(scope, surface) {
    const params = {
        scopeType: scope.type,
        surface,
    };
    if (scope.type === "collection") {
        params.scopeId = scope.id;
    }
    const res = await apiGet("/pin", params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to load pins");
    }
    return res.data.pins;
}
/**
 * Resolve the authenticated account, including the optional `username`
 * handle. Returns `undefined` if no account exists for the session —
 * should never happen post-AuthGate, but the server response permits it.
 */
export async function fetchAccountMe() {
    const res = await apiGet("/account/me");
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to load account");
    }
    return res.data.account;
}
export async function fetchAccountPreferencesMap() {
    const res = await apiGet("/account/preferences/map");
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to load preferences");
    }
    return res.data.preferences;
}
export async function listCollections(options) {
    const params = {};
    if (options?.layerIndex !== undefined)
        params.layerIndex = options.layerIndex;
    const res = await apiGet("/collection/list", params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list collections");
    }
    return res.data;
}
/**
 * Convenience wrapper around `listCollections({ layerIndex: 0 })` for the
 * Groups switcher.
 */
export async function listGroups() {
    return listCollections({ layerIndex: 0 });
}
/**
 * List the 1st-level child collections of a parent collection. Used by
 * the Group view to render subcollections alongside direct blocks.
 */
export async function listCollectionChildren(collectionId) {
    const res = await apiGet(`/collection/${collectionId}/children`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list child collections");
    }
    return res.data.collections;
}
/**
 * Compute the next default Stack name (e.g. "Stack 1", "Stack 2") for
 * a Collection. Used by the multiselect "Create stack" action so the
 * client can pre-fill the title without round-tripping a full create.
 */
export async function getNextStackName(collectionId) {
    const res = await apiGet(`/collection/${collectionId}/next-stack-name`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to get next stack name");
    }
    return res.data.name;
}
/**
 * Suggest a short (1-3 word) Stack name for a Collection given the
 * block IDs about to be stacked. Backed by a Haiku call on the server;
 * silently falls back to the numeric `Stack N` name when the LLM call
 * fails or the block list is empty, so callers can treat this as an
 * always-succeeds operation.
 */
export async function suggestStackName(collectionId, blockIds) {
    const res = await apiPost(`/collection/${collectionId}/suggest-stack-name`, { blockIds });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to suggest stack name");
    }
    return res.data.name;
}
export async function getCollectionDetails(collectionId, options) {
    const params = {};
    if (options?.limit !== undefined)
        params.limit = options.limit;
    if (options?.offset !== undefined)
        params.offset = options.offset;
    const res = await apiGet(`/collection/${collectionId}/details`, params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to get collection details");
    }
    return res.data;
}
export async function listCollectionMembers(collectionId) {
    const res = await apiGet(`/collection/${collectionId}/members`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list collection members");
    }
    return res.data.members;
}
/**
 * Direct + inherited collection ACL. Use for surfaces (e.g. block lip
 * attribution) that must resolve a participant when the collection inherits
 * members from a parent Group and has no direct `CollectionMember` rows.
 */
export async function listCollectionEffectiveMembers(collectionId) {
    const res = await apiGet(`/collection/${collectionId}/effective-members`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list effective collection members");
    }
    return res.data.members;
}
/**
 * List UserAnnotations on a single block. Server filters out soft-deleted
 * rows and returns up to MAX_USER_ANNOTATIONS_PER_BLOCK rows ordered oldest
 * → newest.
 */
export async function listUserAnnotations(blockId) {
    const res = await apiGet(`/user-annotation/by-block/${blockId}`);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list user annotations");
    }
    return res.data.userAnnotations;
}
/**
 * Batched list across multiple blocks in a single round-trip. Used by
 * the stream's TanStack DB collection to prime annotations for every
 * visible card in one request rather than fanning out per block.
 *
 * Server silently drops rows on blocks the caller cannot read, so a
 * partial-deny call still returns the readable subset.
 */
export async function listUserAnnotationsByBlockIds(blockIds) {
    if (blockIds.length === 0)
        return [];
    const res = await apiPost("/user-annotation/by-blocks", { blockIds });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list user annotations by blocks");
    }
    return res.data.userAnnotations;
}
/**
 * Newest-first list of notifications targeting the authenticated user.
 * Powers the in-app notifications popup. The server always scopes by
 * `auth.accountId`, so callers don't pass a userId.
 */
export async function listNotifications(options) {
    const params = {};
    if (options?.limit !== undefined)
        params.limit = options.limit;
    const res = await apiGet("/notification/list", params);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to list notifications");
    }
    return res.data.notifications;
}
